import "../../styles/vesselsettings.css";
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Vessel } from "../../types/Vessel";
import { NumberField } from "../FormFields/RenderNumberField";
import { hideModal } from "../../data/modal";
import { RadioField } from "../FormFields/RenderRadioField";
import { createVessel, updateVessel } from "../../data/vessels";

import {
  Button,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  Add as AddIcon,
  Circle as CircleIcon,
  Close as CloseIcon,
  RemoveSharp as RemoveSharpIcon,
  Save as SaveIcon,
  ScreenLockLandscape as ScreenLockLandscapeIcon,
} from "@mui/icons-material";

const WEIGHT_GROUP_FRONT = "front";
const WEIGHT_GROUP_MIDDLE = "middle";
const WEIGHT_GROUP_BACK = "back";

const WEIGHT_GROUPS = [
  WEIGHT_GROUP_FRONT,
  WEIGHT_GROUP_MIDDLE,
  WEIGHT_GROUP_BACK,
];

const WEIGHT_GROUP_TITLE = {
  front: "Front",
  middle: "Middle",
  back: "Back",
};

const DOCKING_SIDE_PORT = "port";
const DOCKING_SIDE_STARBOARD = "starboard";

const PREFERRED_DOCKING_SIDE_OPTIONS = [
  {
    value: DOCKING_SIDE_PORT,
    label: "Port",
  },
  {
    value: DOCKING_SIDE_STARBOARD,
    label: "Starboard",
  },
];

const VesselGridSettings = ({ vessel }: { vessel: Vessel }) => {
  const dispatch = useDispatch();

  const { handleSubmit, control, setValue, formState } = useForm({
    defaultValues: {
      ...vessel,
      preferredDockingSide: (vessel as any)?.preferredDockingSide ?? DOCKING_SIDE_PORT,
      beamWarnings:
        (vessel as any)?.beamWarnings && (vessel as any).beamWarnings.length > 0
          ? (vessel as any).beamWarnings
          : {
              0: 20,
              1: 35,
              2: 50,
              3: 60,
            },
    },
  });

  const { isSubmitting } = formState;

  const vesselExistingPostions =
    (vessel as any)?.positions && (vessel as any).positions.length > 0
      ? (vessel as any).positions[0]
      : [];
  const vesselExistingBarriers =
    (vessel as any)?.barriers && (vessel as any).barriers.length > 0
      ? (vessel as any).barriers
      : [];

  const [vesselPositions, setVesselPositions] =
    useState<number[]>(vesselExistingPostions);
  const [vesselBarriers, setVesselBarriers] =
    useState<any[]>(vesselExistingBarriers);
  const [vesselDummies, setVesselDummies] = useState<[number, number][]>(
    Array.isArray((vessel as any)?.dummies) ? (vessel as any).dummies : []
  );

  const [grids, setGrids] = useState(vesselPositions.length);

  const gridVesselPosition = (position: number) => {
    if (
      !vesselPositions ||
      vesselPositions.length === 0 ||
      vesselPositions[position] === undefined
    ) {
      return 0;
    }

    return vesselPositions[position];
  };

  const hasPositionBarrier = (axis: any) => {
    const requirementsObtained: any[] = [];

    if (!vessel || ((vessel as any).barriers && (vessel as any).barriers.length === 0)) {
      return false;
    }

    vesselBarriers.forEach((barrierPosition: any) => {
      requirementsObtained.push(
        JSON.stringify(barrierPosition) === JSON.stringify(axis)
      );
    });

    return requirementsObtained.includes(true);
  };

  const addColumn = () => {
    setGrids((current) => current + 1);
  };

  const isDummy = (x: number, y: number) =>
    vesselDummies.some(([dx, dy]) => dx === x && dy === y);

  const toggleDummy = (x: number, y: number) => {
    setVesselDummies((prev) => {
      const exists = prev.some(([dx, dy]) => dx === x && dy === y);

      if (exists) {
        return prev.filter(([dx, dy]) => !(dx === x && dy === y));
      }

      return [...prev, [x, y]];
    });
  };

  const pruneDummies = (nextPositions: number[]) => {
    setVesselDummies((prev) =>
      prev.filter(([x, y]) => {
        const colIndex = x - 1;
        const colHeight = nextPositions[colIndex] || 0;

        return (
          colIndex >= 0 &&
          colIndex < nextPositions.length &&
          y >= 1 &&
          y <= colHeight
        );
      })
    );
  };

  const addGridPosition = (position: number) => {
    let updatedPositions = vesselPositions.map((vesselPosition, index) => {
      if (position === index) {
        return vesselPosition + 1;
      }

      return vesselPosition;
    });

    if (gridVesselPosition(position) === 0) {
      updatedPositions.push(1);
    }

    setVesselPositions(updatedPositions);
  };

  const removeGridPosition = (position: number) => {
    let updatedPositions = vesselPositions.map((vesselPosition, index) => {
      if (position === index) {
        return Math.max(0, vesselPosition - 1);
      }

      return vesselPosition;
    });

    if (updatedPositions[position] === 0) {
      setGrids((current) => Math.max(0, current - 1));
      updatedPositions = updatedPositions.filter(
        (updatedPosition) => updatedPosition > 0
      );
    }

    setVesselPositions(updatedPositions);
    pruneDummies(updatedPositions);
  };

  const addBarrierPosition = (axis: any) => {
    setVesselBarriers([...vesselBarriers, axis]);
  };

  const removeBarrierPosition = (axis: any) => {
    const updatedBarrierPosition = vesselBarriers.filter(
      (barrierPosition) =>
        JSON.stringify(barrierPosition) !== JSON.stringify(axis)
    );

    setVesselBarriers(updatedBarrierPosition);
  };

  const weightGroupOptions = () => {
    return WEIGHT_GROUPS.map((weightGroup) => ({
      value: weightGroup,
      label: undefined,
    }));
  };

  const showBeamWarningsByLoop = (loop: number, fieldLabel: string) => {
    return (
      <Grid
        item
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "5px",
          marginBottom: "10px",
          marginTop: "10px",
        }}
      >
        <Grid
          item
          sx={{
            display: "flex",
            minWidth: "75px",
            alignItems: "center",
          }}
        >
          {[...Array(loop ?? 0)].map((x, i) => (
            <CircleIcon key={`beam-dot-${loop}-${i}`} fontSize="10px" />
          ))}
        </Grid>

        <Grid item xs={12} sm={2}>
          <NumberField
            name={`beamWarnings.${loop - 1}`}
            label={fieldLabel}
            control={control}
            required
          />
        </Grid>

        <Typography>T</Typography>
      </Grid>
    );
  };

  const onSubmit: SubmitHandler<Vessel> = (data: any) => {
    let vesselData = data;
    const vesselPositionsWithLayers: number[][] = [];

    [...Array(data.layers ?? 0)].forEach(() => {
      vesselPositionsWithLayers.push(vesselPositions);
    });

    if (vesselPositionsWithLayers.length > 0) {
      vesselData = {
        ...vesselData,
        positions: vesselPositionsWithLayers,
      };
    }

    vesselData = {
      ...vesselData,
      preferredDockingSide: data.preferredDockingSide || DOCKING_SIDE_PORT,
      dummies: vesselDummies.length > 0 ? vesselDummies : [],
    };

    if (vesselBarriers.length > 0) {
      vesselData = {
        ...vesselData,
        barriers: vesselBarriers,
      };
    } else {
      vesselData = {
        ...vesselData,
        barriers: [],
      };
    }

    if (vessel) {
      dispatch(updateVessel(vesselData) as any);
    } else {
      dispatch(createVessel(vesselData) as any);
    }
  };

  useEffect(() => {
    setValue("layers", (vessel as any)?.positions?.length ?? 0);
    setValue(
      "preferredDockingSide",
      (vessel as any)?.preferredDockingSide ?? DOCKING_SIDE_PORT
    );
  }, [vessel, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Typography variant="h5">Vessel positions</Typography>

      <Grid container className="vessel-settings-container">
        <Grid item className="vessel-settings-main-wrapper">
          <Grid className="vessel-settings-wrapper" item>
            <Grid className="vessel-settings-ship-bow" item />

            <Grid className="vessel-settings-ship-body" item>
              {grids > 0 &&
                [...Array(grids)].map((container, index) => (
                  <React.Fragment key={`col-${index}`}>
                    <Grid
                      className="vessel-settings-ship-grid-wrapper"
                      item
                      data-grid-position={index}
                    >
                      <Typography className="vessel-settings-axis-label">
                        {index + 1}
                      </Typography>

                      {gridVesselPosition(index) > 0 &&
                        [...Array(gridVesselPosition(index))].map(
                          (grid, gridIndex) => {
                            const x = index + 1;
                            const y = gridIndex + 1;
                            const dummy = isDummy(x, y);

                            return (
                              <Grid
                                onClick={() => removeGridPosition(index)}
                                item
                                className={`vessel-settings-grid ${
                                  dummy ? "is-dummy" : ""
                                }`}
                                key={`grid-${index}-${gridIndex}`}
                              >
                                <Tooltip
                                  title={dummy ? "Unset dummy" : "Set as dummy"}
                                  arrow
                                >
                                  <button
                                    type="button"
                                    className="vessel-settings-grid__dummy-btn"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      toggleDummy(x, y);
                                    }}
                                  >
                                    <ScreenLockLandscapeIcon
                                      fontSize="inherit"
                                      color={dummy ? "primary" : "action"}
                                    />
                                  </button>
                                </Tooltip>

                                <RemoveSharpIcon
                                  className="grid-remove"
                                  fontSize="small"
                                />
                              </Grid>
                            );
                          }
                        )}

                      {gridVesselPosition(index) === 0 ||
                      gridVesselPosition(index) < 6 ? (
                        <Grid
                          item
                          className="vessel-settings-grid vessel-settings-grid--add"
                          onClick={() => addGridPosition(index)}
                        >
                          <AddIcon fontSize="small" />
                        </Grid>
                      ) : null}
                    </Grid>

                    {index !== grids - 1 &&
                    !hasPositionBarrier([index + 1, index + 2]) ? (
                      <Grid
                        onClick={() => addBarrierPosition([index + 1, index + 2])}
                        item
                        className="vessel-settings-column-button vessel-settings-column-button--add-barrier"
                      >
                        <AddIcon fontSize="small" />
                      </Grid>
                    ) : null}

                    {hasPositionBarrier([index + 1, index + 2]) ? (
                      <Grid
                        onClick={() =>
                          removeBarrierPosition([index + 1, index + 2])
                        }
                        className="vessel-settings-column-button vessel-settings-column-button--remove-barrier"
                        item
                      >
                        <RemoveSharpIcon
                          className="grid-remove"
                          fontSize="small"
                        />
                      </Grid>
                    ) : null}
                  </React.Fragment>
                ))}
            </Grid>

            <Grid
              onClick={() => addColumn()}
              item
              className="vessel-settings-column-button vessel-settings-column-button--add-column"
            >
              <AddIcon fontSize="small" />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12} sm={12}>
        <Grid
          item
          xs={12}
          className="form-fields-container"
          sx={{
            marginTop: "15px",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Grid item xs={12} sm={2}>
            <NumberField
              name="layers"
              label="Number of layers"
              control={control}
              required
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller
              name="preferredDockingSide"
              control={control}
              defaultValue={(vessel as any)?.preferredDockingSide ?? DOCKING_SIDE_PORT}
              render={({ field }) => (
                <FormControl fullWidth size="small">
                  <InputLabel id="preferred-docking-side-label">
                    Docking side
                  </InputLabel>

                  <Select
                    {...field}
                    labelId="preferred-docking-side-label"
                    label="Preferred docking side"
                    value={field.value || DOCKING_SIDE_PORT}
                  >
                    {PREFERRED_DOCKING_SIDE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12} sm={12}>
        <Divider />
      </Grid>

      <Grid container className="vessel-settings-container">
        <Grid item xs={12} sm={12}>
          <Typography variant="h5">Beam warnings</Typography>
          {showBeamWarningsByLoop(1, "1st beam warning")}
          {showBeamWarningsByLoop(2, "2nd beam warning")}
          {showBeamWarningsByLoop(3, "3rd beam warning")}
          {showBeamWarningsByLoop(4, "4th beam warning")}
        </Grid>

        <Grid item xs={12} sm={12}>
          <TableContainer>
            <Table aria-label="container table dense">
              <TableHead>
                <TableRow>
                  <TableCell>Weight Group</TableCell>

                  {grids > 0 &&
                    [...Array(grids)].map((grid, index) => (
                      <TableCell align="center" key={`wg-col-${index}`}>
                        {index + 1}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>

              <TableBody>
                <TableRow>
                  <TableCell>
                    <Grid item>
                      <Grid item sx={{ height: "45px", paddingTop: "8px" }}>
                        {WEIGHT_GROUP_TITLE[WEIGHT_GROUP_FRONT]}
                      </Grid>

                      <Grid item sx={{ height: "45px", paddingTop: "4px" }}>
                        {WEIGHT_GROUP_TITLE[WEIGHT_GROUP_MIDDLE]}
                      </Grid>

                      <Grid item>{WEIGHT_GROUP_TITLE[WEIGHT_GROUP_BACK]}</Grid>
                    </Grid>
                  </TableCell>

                  {grids > 0 &&
                    [...Array(grids)].map((grid, index) => (
                      <TableCell align="center" key={`wg-radio-${index}`}>
                        <RadioField
                          name={`weightGroup.${index}`}
                          control={control}
                          options={weightGroupOptions()}
                        />
                      </TableCell>
                    ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      <Grid
        item
        xs={12}
        className="button-wrapper"
        sx={{ borderTop: "transparent !important" }}
      >
        <Button
          onClick={() => {
            dispatch(hideModal() as any);
          }}
          disabled={isSubmitting}
          variant="contained"
        >
          <CloseIcon fontSize="inherit" />
          &nbsp;Close
        </Button>

        <Button type="submit" disabled={isSubmitting} variant="contained">
          <SaveIcon fontSize="inherit" />
          &nbsp;Save
        </Button>
      </Grid>
    </form>
  );
};

export default VesselGridSettings;